// Keeping a phone's keyboard UP across a back navigation (2026-09-12, user
// report: "when the reader goes back from a Lab answer to the Lab view the
// keyboard isn't opened").
//
// iOS Safari raises the keyboard for a programmatic focus() only inside a
// user gesture. Tapping a segment or the sparkle opens the Lab view in
// that tap (React flushes the click synchronously, the composer's layout
// effect focuses inside it) and the keys come up. "Go back" is different:
// the tap calls history.goBack(), the browser fires the traversal in a
// LATER task, the home mounts there, and its focus() lands outside any
// gesture — the field focuses, the keys stay down.
//
// The platform's one door through this is also its documented rule: while
// an editable element already holds focus WITH the keyboard showing, focus
// may move to another editable programmatically and the keyboard follows.
// So the back tap first focuses this ANCHOR — a one-pixel, invisible,
// never-tabbable textarea that lives outside the routes so it survives the
// page swap — which raises the keys inside the gesture; then the traversal
// runs, the home mounts its Lab view, and the composer's own mount focus
// takes the keys over. If the route that arrives claims nothing (the home
// opened on another view, a cold chunk showed the fallback first), the
// anchor lets the keys go again in the next route commit, so a handoff
// can never leave a keyboard standing over the wrong screen. Mounted once
// by navigation/RouterConfig; `handoffKeyboard()` is what a back control
// calls, inside its click, when it knows a composer is about to mount
// (pages/Lab: the answer page going back to the home's Lab view).
//
// 16px type on the anchor: iOS zooms the page for a focused field under
// 16px, and this one is focused on purpose.
import { useEffect, useLayoutEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

let anchor = null;

// focus the anchor — inside a user gesture, or it does nothing useful;
// true when the anchor holds focus afterwards, so a caller can wait on it
export const handoffKeyboard = () => {
  if (anchor) anchor.focus({ preventScroll: true });
  return holdsHandoff();
};
export const holdsHandoff = () => !!anchor && document.activeElement === anchor;

// an anchor still holding the keys this long after a route commit is one
// nobody wanted — longer than a composer takes to claim them
// (pages/Lab/AskBar KEYS_TIMEOUT_MS + the keyboard's own settle)
export const RELEASE_MS = 900;

export default function KeyboardHandoff() {
  const ref = useRef(null);
  const location = useLocation();
  // a LAYOUT effect: it renders before the routes, so the anchor is
  // registered before any composer's own layout effect asks for it — in
  // the same commit, on the very first one
  useLayoutEffect(() => {
    const el = ref.current;
    anchor = el;
    return () => {
      if (anchor === el) anchor = null;
    };
  }, []);
  // after every route commit: a composer that mounted with the keys
  // already up has claimed them by now (a layout effect); one that is
  // raising them on the anchor claims within KEYS_TIMEOUT_MS; an anchor
  // still holding them after RELEASE_MS means nobody wanted them
  useEffect(() => {
    if (!holdsHandoff()) return undefined;
    const timer = setTimeout(() => {
      if (holdsHandoff()) anchor.blur();
    }, RELEASE_MS);
    return () => clearTimeout(timer);
  }, [location]);
  return (
    <textarea
      ref={ref}
      className="kb-anchor"
      aria-hidden="true"
      tabIndex={-1}
      rows={1}
      autoComplete="off"
    />
  );
}
